/**
 * executiveDecisionRollup — the LIVE Go/No-Go Decision Guide engine.
 *
 * `buildExecutiveDecisionRollup(args)` is the deterministic, LLM-FREE,
 * read-only reconciliation behind the Executive Summary report's authoritative
 * GO / NO-GO call. It mirrors `deriveSiteVerdict`'s PURITY so it is unit-testable
 * in isolation like `buildPathToGoState`: it mutates nothing, takes no callbacks,
 * and returns the SAME output for the SAME inputs.
 *
 * It REUSES — never re-implements — the platform's existing deterministic
 * engines so the headline call can never drift from the dots / pills / bars /
 * portfolio snapshot / Path-to-GO wizard:
 *   - PATH 0  deriveSiteVerdict(composite, null, bandConfig)   → the BASE band.
 *   - PATH 1  evaluateSiteDataAdequacy(composite)              → adequacy gate.
 *   - PATH 1b explicit grid-versus-BTM strategy                 → intent gate.
 *   - PATH 2  getDisplayVerdict(report) + sourceFloorPolicy    → per-source floor.
 *   - PATH 3  capTierByFirmCapacity(tier, intent)              → firm-capacity cap.
 *   - PATH 5  resolveSimFeasibilityCap(simEvidence)            → sim-feasibility cap.
 *   - PATH 6  unmitigated critical RiskItems                   → critical-finding cap.
 *   - PATH 4  requiredReportsForGo(intent) + reportSatisfiesGo → completeness cap.
 *
 * MONOTONIC-DOWN reconciliation: the BASE band (PATH 0) is the CEILING. Every
 * cap can only LOWER it, never raise it — exactly as `getDisplayVerdict` stacks
 * `capTierByFirmCapacity` + the EA8 evidence-tier cap (each pushes toward
 * caution only). `bindingConstraints` collects ONLY the caps that actually FIRED
 * (were the binding constraint); an empty `bindingConstraints` ⇔ the base band
 * stood (every cap was non-binding), mirroring the Financial
 * DecisionBandSection's "empty when Viable" semantics.
 *
 * EXECUTION ORDER is 1 → 1b → 2 → 3 → 5 → 6 → 4, deliberately NOT the numeric
 * order. Caps run STRONGEST-FIRST so that each subsequent strict-less-than guard
 * suppresses a weaker, redundant cap rather than stacking a second note for the
 * same band. PATH 4 (absence) is the weakest and therefore last.
 *
 * HOW FAR A CAP MAY PUSH is a per-source property, not a global one. PATH 2
 * resolves each source through `sourceFloorPolicy`: 'hard' sources (physics,
 * permitting, financeability) map a red verdict to NO-GO, while 'mitigable'
 * sources (connectivity, procurement, public sentiment) cap at CONDITIONAL —
 * they still block an unconditional GO but can never assert NO-GO alone, since
 * each names a problem time, capital, routing, or engagement can address.
 * PATH 6 is CONDITIONAL-only for a different reason: `RiskItem` rows are
 * LLM-authored, and while the schema-constrained severity enum makes them
 * admissible structured evidence, narrative must never hold the strongest
 * verdict in the system.
 *
 * CONCEPT A/B GUARDRAIL: PATH 0 is called with `executiveSummaryStatus = null`
 * on purpose. THIS rollup IS the authoritative exec-summary verdict, so it must
 * NOT read a stale persisted exec-summary narrative claim back into its own base
 * (the circular-narrative trap).
 */
import { deriveIntentForSelectedAssessment, GRID_LED_PLANNING_FIRM_QUALIFICATION_NOTICE } from './generationImpact/selectedAssessmentIntent';
import { REPORT_TYPE_REGISTRY, reportTypeStage, type ReportTypeId } from '../config/reportTypes';
import type { SiteEntity } from '../types/site';
import type { SiteRagReport, ProjectIntent } from '../types/siteRag';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import type { ScreeningOutcome, SiteComposite } from '../types/portfolioComposite';
import { DECISION_BAND_LABEL, type DecisionBandConfig, VERDICT_WITHHELD_LABEL } from './portfolio/decisionBands';
import {
  deriveSiteVerdict,
  evaluateSiteDataAdequacy,
} from './portfolio/deriveSiteVerdict';
import { computeSiteComposite } from './portfolio/compositeScorer';
import { PILLARS, isAdvisoryPillar, isAdvisoryReportType } from './portfolio/pillarRegistry';
import { PORTFOLIO_CONFIG } from './portfolio/portfolioConfig';
import {
  getDisplayVerdict,
  capTierByFirmCapacity,
  type VerdictTier,
} from './reportVerdict';
import {
  summarizeIntentFeasibility,
  type PhaseFeasibilityVerdict,
} from './phaseFeasibility';
import {
  requiredReportsForGo,
  sourceFloorPolicy,
  type SourceFloorPolicy,
} from './pathToGo/mandatoryReports';
import {
  buildReportPlan,
  latestUsableSavedReportOfType,
  planItemCompletesStage,
  type BuildReportPlanInput,
} from './pathToGo/reportPlan';
import {
  assessGenerationImpactTestFitApplicability,
} from './generationImpact/testFitApplicability';
import { deriveGenerationDecisionContribution } from './generationImpact/decisionContribution';
import { resolvePromotedGenerationScenario } from './generationImpact/promotedScenario';
import { selectedGridLedPlanningReadinessReason } from './generationImpact/gridLedStandbyPlanningReadiness';
import { isUnmitigatedRisk, scoringRisksForReport, ENVIRONMENTAL_RISK_MATRIX_ID_PREFIX } from './portfolio/scoringRisks';
import { enqueueCoverageRefusalFor } from './audit/enqueueCoverageGate';

/**
 * The derived decision band used across the verdict surfaces. Mirrors
 * `DerivedSiteVerdict.band` ('go' | 'conditional-high' | 'conditional' |
 * 'no-go') plus `null` for a withheld / not-yet-computed verdict.
 */
export type DerivedBand =
  | 'go'
  | 'conditional-high'
  | 'conditional'
  | 'no-go'
  | null;

/** One mandatory-source row in the per-source verdict matrix. */
export interface PerSourceVerdictRow {
  reportType: ReportTypeId;
  /** The source's display tier ('none' when the report is absent). */
  tier: VerdictTier;
  /** Human label for the tier (em-dash when absent / unscored). */
  label: string;
  /** True when a report of this mandatory type exists for the site. */
  present: boolean;
  /**
   * STAGE 1.5 — false for a GENERATED source the intent never made mandatory.
   *
   * Such a report still carries real evidence, so it caps the call at
   * CONDITIONAL, but it may never assert NO-GO on its own: PATH 2 resolves a
   * `mandatory: false` row as 'mitigable' regardless of the type's own
   * `sourceFloorPolicy`. Optional so historical frozen rollups (which only ever
   * held mandatory rows) keep deserialising unchanged and read as mandatory.
   */
  mandatory?: boolean;
  /**
   * False when the latest artifact is stale, held, authority-less, or otherwise
   * cannot satisfy the live GO gate. Optional for historical frozen rollups.
   */
  qualifies?: boolean;
}

/**
 * TWO-STAGE DEVELOPER DECISION PIPELINE (Phase 2, owner decisions 2 and 4,
 * 2026-09-06) — the two stages of the decision basis, read off the same
 * mandatory set and per-source qualification the rollup already computes.
 * Stage 1 (early screening) must be complete, and Stage 2 (mature validation)
 * must be unlocked by a promoted scenario and complete, before a band is
 * published; until then the composite stays `Decision pending`. Presence is
 * judged by the composite's own hold; CURRENCY is judged here (a stale
 * required analysis leaves its stage incomplete).
 */
export interface DecisionStageRollup {
  stage: 1 | 2;
  /** Mandatory analyses of this stage, in required-set order. */
  requiredTypes: ReportTypeId[];
  /** Current stage work; completed unmeasured screening does not imply positive GO evidence. */
  qualifiedTypes: ReportTypeId[];
  /** The subset that does not — missing, stale or non-qualifying. */
  outstandingTypes: ReportTypeId[];
  complete: boolean;
  /** Stage 2 only: no promoted, still-valid generation scenario on a site that
   *  has not declared grid-only. Always false for Stage 1. */
  locked: boolean;
  /**
   * Stage 1 only, once every required screening report is present: the
   * SCREENING RESULT read from the per-source display tiers above — red
   * declines, amber carries concerns into mature validation, all green is
   * clear. Its own vocabulary, never a band (two-stage review F3).
   */
  outcome?: ScreeningOutcome;
  /** Stage 1 only: the amber (or red) screening reports behind the outcome. */
  concernTypes?: ReportTypeId[];
  /** Stage 1 only: the composite's SCREENING SCORE (0–100) — never a band. */
  score?: number;
  /** Stage 1 only: share of the Stage-1-fed pillar weight measured. */
  scoreCoverage?: number;
}

export interface DecisionStagesRollup {
  screening: DecisionStageRollup;
  validation: DecisionStageRollup;
}

export interface DecisionGuideRollup {
  /** Two-stage status. Absent only on snapshots persisted before Phase 2. */
  stages?: DecisionStagesRollup;
  /** Final monotonic-down band (the BASE band lowered by any firing cap). */
  band: DerivedBand;
  /** Final human label (e.g. 'Conditional', 'No-Go', 'Insufficient evidence'). */
  label: string;
  /**
   * Ordered audit trail — the base-band line followed by each binding
   * constraint's explanation. Mirrors DecisionBandSection's `fa.rationale`.
   */
  rationale: string[];
  /**
   * ONLY the caps that actually fired (were the binding constraint), in path
   * order (1 → 4). Empty ⇔ the base band stood.
   */
  bindingConstraints: string[];
  /** The mandatory-source verdict matrix (one row per required report type). */
  perSourceVerdicts: PerSourceVerdictRow[];
  /**
   * CONNECTIVITY-ADVISORY (2026-08-25) — informational source rows that are
   * DISPLAYED but NEVER counted: no PATH iterates this array, so an advisory
   * row is structurally unable to cap, floor, or complete the verdict (versus
   * a filter guard someone could forget). Today it carries at most the
   * Connectivity Feasibility Study's verdict when one exists while
   * `CONNECTIVITY_GATES_GO` is false. Optional so frozen pre-change
   * `executiveDecisionRollupAtGen` snapshots stay parseable — consumers must
   * read it as `advisoryVerdicts ?? []`.
   */
  advisoryVerdicts?: PerSourceVerdictRow[];
  /** The worst per-phase firm-capacity verdict that informed PATH 3. */
  firmCapacityVerdict: PhaseFeasibilityVerdict | null;
  /** The adequacy gate result (PATH 1), surfaced for the read-only UI. */
  adequacy: { adequate: boolean; reason: string | null; coverageRatio: number };
  /** True when every mandatory report type is present (PATH 4). */
  mandatorySatisfied: boolean;
}

/**
 * Sim P2.12-S2/S3 — the sim-quality signal PATH 5 caps on. ALL fields optional:
 * a present-but-poor sim caps the verdict; an absent sim (or all-undefined
 * fields) is a NEUTRAL no-op (no-assert-on-no-evidence). The mapper
 * `simQualityFromAnnualYieldEvidence` is the single annual-yield producer,
 * sharing one "converged" definition with the finance path.
 */
export interface SimQualityEvidence {
  /** Which engine produced the run (provenance only; does not change the cap). */
  source: 'annual-yield' | 'eo';
  /**
   * Honest convergence flag. `false` ⇒ the modelled cycle did not close.
   * `undefined` is NEUTRAL — an unknown convergence state is NEVER treated as a
   * failure. Derived iff hoursConverged >= hoursEvaluated.
   */
  converged?: boolean;
  /** Fraction of evaluated hours that converged (0..1). */
  convergenceRate?: number;
  /** Realised capacity factor from the hour-by-hour dispatch (0..1). */
  capacityFactor?: number;
  /** S3 — relative energy/mass closure residual (EO conservation). */
  closureResidualRel?: number;
  /** Originating run id, for traceability in the binding-constraint note. */
  sourceRunId?: string;
}

export interface BuildExecutiveDecisionRollupArgs {
  site: SiteEntity;
  sed: SiteEvidenceDocument | null;
  /** ALL sibling reports for the site (already filtered by the caller). */
  reports: SiteRagReport[];
  intent: ProjectIntent | undefined;
  bandConfig: DecisionBandConfig | null;
  /** Precomputed oracle composite; recomputed when absent (byte-identical). */
  composite: SiteComposite | null;
  /**
   * Sim P2.12-S2/S3 — OPTIONAL sim-quality evidence (PATH 5). When absent /
   * null / all-undefined, PATH 5 is a NEUTRAL no-op and the rollup is
   * BYTE-IDENTICAL to before this slice. Only a present-but-poor sim caps.
   */
  simEvidence?: SimQualityEvidence | null;
  /**
   * TEST-ONLY baseline switch: `false` computes the pre-Phase-2 band without
   * the two-stage hold so the invariant "the two-stage band is never higher"
   * can be asserted against the same inputs. Production callers never set it.
   */
  twoStageHold?: boolean;
  /** Live semantic pointer authority for stage completion; omitted callers retain report mode. */
  analysisContext?: Pick<BuildReportPlanInput,
    'analysisGateEnabled' | 'analysisSnapshots' | 'currentAnalysisInputsByDomain'>;
}

// ── Sim-feasibility cap thresholds (PATH 5) — FLAGGED PRODUCT DECISIONS ─────────
//
// FLAGGED FOR REVIEW: each constant below is a PRODUCT threshold (same style as
// MIN_PILLAR_COVERAGE in deriveSiteVerdict.ts). Defaults are PROPOSED with the
// stated provenance; William signs off via the before/after impact table before
// ship. NONE is a fabricated physics number asserted as truth.

/**
 * Band ceiling imposed when a sim run is HARD non-converged (converged===false).
 * FLAGGED: a non-converged solve is an ABSENCE of feasibility evidence, not
 * proof of infeasibility — so the conservative, trust-aligned default WITHHOLDS
 * the unconditional GO (caps to 'conditional') rather than asserting a hard
 * fail. The user's North Star is "no confident verdict on bad evidence", which
 * 'conditional' (plus the explicit binding-constraint note) satisfies. William
 * may escalate to 'no-go' for the feasibility/power report family.
 * Provenance: NEW product decision (no frozen reference). Owner: William.
 */
export const HARD_NONCONVERGENCE_CEILING: Exclude<DerivedBand, null> = 'conditional';

/**
 * Minimum fraction of evaluated hours that must converge before an unconditional
 * GO is assertable. Below this the annual yield is only partially modelled.
 * FLAGGED: NEW product threshold — NOT mirrored from physics. Proposed
 * conservatively at 0.95 (>=95% of hours). Owner: William.
 */
export const CONVERGENCE_RATE_FLOOR = 0.95;

/**
 * Capacity-factor floor below which LCOE is dominated by fixed-cost-per-MWh and
 * the economics are structurally suspect.
 * FLAGGED: mirrors the frozen oracle's INTENT — backend/economics/production_hook.py:51
 * `capacity_factor_floor=0.05`. Stated assumption, not invented. Owner: William.
 */
export const CAPACITY_FACTOR_FLOOR = 0.05;

/**
 * S3 (LIVE — closure-residual cap). Relative energy/mass closure-residual
 * tolerance above which the modelled cycle does not conserve within tolerance.
 * FLAGGED: mirrors CLAUDE.md Rule 3 ("Flag deviations above 0.1%" for
 * conservation closure) → 0.001. Stated assumption. Owner: William.
 * WIRED: Rule 4 of `resolveSimFeasibilityCap` caps on `sim.closureResidualRel`,
 * which `annualYieldToPathway` threads from the EO conservation residuals.
 */
export const CLOSURE_RESIDUAL_TOLERANCE = 0.001;

// ── Tier ↔ band ladder ───────────────────────────────────────────────────────

/**
 * Rank the derived band on a worst→best ladder so a cap can decide whether it
 * would actually LOWER the running band. Higher rank = better (closer to GO).
 * `null` (withheld) sits at the very bottom — nothing is below a withheld call.
 *
 * ORDERING IS AUTHORITATIVE FROM `decisionBands.DEFAULT_BANDS`, which grades on
 * the RISK scale (`toDecisionScore = 6 - benefitComposite`, higher = worse):
 *
 *   GO 1.0–2.0  <  CONDITIONAL 2.01–3.0  <  CONDITIONAL HIGH 3.01–4.0  <  NO-GO 4.01–5.0
 *
 * so CONDITIONAL HIGH ("Enhanced diligence required. Executive approval
 * needed.", colour orange) is WORSE than CONDITIONAL ("Viable with targeted
 * mitigation.", colour amber). This table previously ranked them the other way
 * round, which inverted the two middle bands and broke the monotonic-down
 * contract in two ways:
 *
 *   1. A cap resolving to `conditional` applied to a site sitting at
 *      `conditional-high` passed the strict-less-than guard and RAISED it —
 *      a cap that improves the verdict, which no cap may ever do. PATH 4
 *      (mandatory-completeness) was the clearest case: missing diligence could
 *      promote a CONDITIONAL HIGH site to CONDITIONAL.
 *   2. A composite that genuinely worsened across the 3.0 boundary read to the
 *      rollup as an IMPROVEMENT, so legitimate caps were skipped.
 *
 * Verified against the independent ladder in `distanceToGo.test.ts`
 * (`{ go: 0, conditional: 1, conditionalHigh: 2, noGo: 3 }`, worst-highest),
 * which agrees with `decisionBands` and disagreed with the old table here.
 */
export const BAND_RANK: Record<Exclude<DerivedBand, null>, number> = {
  'no-go': 0,
  'conditional-high': 1,
  conditional: 2,
  go: 3,
};

/**
 * EXPORTED so tests and the verdict-stability replay harness consume the SAME
 * ladder rather than re-declaring it. Both previously kept private copies, and
 * both copies inherited the inversion documented above — so the very tests and
 * the very tool meant to detect a bad band movement were grading against the
 * wrong order and could not have caught it.
 */
export function rankBand(band: DerivedBand): number {
  return band === null ? -1 : BAND_RANK[band];
}

/**
 * Map a cap tier ('red'|'amber') to the band ceiling it imposes, under the
 * source's floor policy.
 *
 * - 'hard'      → red imposes NO-GO; amber imposes CONDITIONAL (a source or
 *                 firm-capacity amber is CONDITIONAL, not conditional-high).
 * - 'mitigable' → CONDITIONAL for both. A reversible constraint still blocks an
 *                 unconditional GO but can never assert NO-GO on its own.
 *
 * 'green' / 'none' never impose a cap and are filtered before this call.
 */
function tierToCapBand(
  tier: VerdictTier,
  policy: SourceFloorPolicy = 'hard',
): Exclude<DerivedBand, null> {
  if (policy === 'mitigable') return 'conditional';
  return tier === 'red' ? 'no-go' : 'conditional';
}

/**
 * PATH 6 — labels of a report's UNMITIGATED critical risks.
 *
 * Reads only STRUCTURED rows: `report.risks[]` (schema-constrained `severity`
 * enum, required `mitigation`) plus — since P1.7 — the environmental report's
 * `environmentalRiskMatrix` rows whose free-text severity is EXACTLY one of
 * the four enum words (`scoringRisks.ts` applies that constraint after the
 * fact; anything else admits nothing). A risk carrying a real mitigation is
 * priced work, not open exposure, and does not cap.
 *
 * `scoringRisksForReport` / `isUnmitigatedRisk` are the SAME functions the
 * composite's finding-severity penalty reads (R4a), so the composite and this
 * cap cannot disagree about which rows exist or whether one is open.
 */
function unmitigatedCriticalRiskLabels(report: SiteRagReport): string[] {
  const out: string[] = [];
  for (const risk of scoringRisksForReport(report)) {
    if (risk?.severity !== 'critical') continue;
    if (!isUnmitigatedRisk(risk)) continue;
    const label = risk.label?.trim();
    if (!label) continue;
    // Review M5 — a row projected from the environmental Risk Assessment matrix
    // is not in the report's Risk register; say where the reader will find it.
    const fromMatrix = typeof risk.id === 'string' && risk.id.startsWith(ENVIRONMENTAL_RISK_MATRIX_ID_PREFIX);
    out.push(fromMatrix ? `${label} (risk matrix)` : label);
  }
  return out;
}

// ── Public entry point ─────────────────────────────────────────────────────────

/**
 * Build the LIVE Go/No-Go Decision Guide rollup. Pure + deterministic.
 *
 * @param composite optional precomputed `SiteComposite` — pass the genuine
 *                  oracle output to avoid a recompute; recomputed via the real
 *                  `computeSiteComposite` oracle when absent.
 */
export function buildExecutiveDecisionRollup(
  args: BuildExecutiveDecisionRollupArgs,
): DecisionGuideRollup {
  const { site, sed, reports, intent: canonicalIntent, bandConfig, simEvidence } = args;
  const intent = deriveIntentForSelectedAssessment(canonicalIntent, site.generationAssessmentSelection);

  // Composite (oracle) — recompute only when the caller did not pass one, the
  // SAME path buildPathToGoState uses (byte-identical verdict/distance).
  const composite =
    args.composite ??
    computeSiteComposite({
      site,
      customerId: site.companyId ?? 'unknown',
      reports,
      evidence: sed,
      bandConfig,
      analysisContext: args.analysisContext,
    });

  // ── PATH 0 — BASE band (the ceiling). ───────────────────────────────────────
  // executiveSummaryStatus = null on purpose (concept A/B guardrail): this
  // rollup IS the authoritative verdict, so it must not read a stale persisted
  // exec-summary narrative claim back into its own base.
  const base = deriveSiteVerdict(composite, null, bandConfig ?? undefined);

  const perSourceVerdicts: PerSourceVerdictRow[] = [];
  const bindingConstraints: string[] = [];
  const rationale: string[] = [];
  if (site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') rationale.push(GRID_LED_PLANNING_FIRM_QUALIFICATION_NOTICE);

  // ── PATH 1 — ADEQUACY GATE (can only LOWER → withheld). ──────────────────────
  // deriveSiteVerdict already folds the adequacy gate in (band=null when
  // inadequate), but we ALSO surface it explicitly so the read-only UI carries
  // {adequate, reason, coverageRatio}. When inadequate we SHORT-CIRCUIT to a
  // withheld result — a withheld verdict is already the floor, so the other caps
  // are not asserted on top of it (mirrors deriveSiteVerdict's own short-circuit
  // and the orchestrator's adequacy-first stance).
  const adequacyRaw = composite
    ? evaluateSiteDataAdequacy(composite)
    : { adequate: false, reason: 'Not yet computed', coverageRatio: 0 };
  const adequacy = {
    adequate: adequacyRaw.adequate,
    reason: adequacyRaw.reason,
    coverageRatio: adequacyRaw.coverageRatio,
  };

  // Mandatory report set — needed for the matrix even on a withheld short-circuit.
  // This uses the same applicability inputs and the same strict satisfaction
  // predicate as Path to GO; a stale or authority-less artifact is viewable but
  // never counted as current decision evidence.
  const powerAvailabilityPreferences =
    site.typedPreferencesDefaults?.['power-availability'];
  const applicability = assessGenerationImpactTestFitApplicability({
    projectIntent: intent,
    powerAvailabilityPreferences,
  });
  const decisionSite = intent === site.projectIntent
    ? site
    : { ...site, projectIntent: intent };
  const mandatory = requiredReportsForGo(
    intent,
    powerAvailabilityPreferences,
    decisionSite,
  );
  const calculationTypes = [...new Set<ReportTypeId>([
    ...mandatory,
    ...reports.map(report => (report.reportType ?? 'feasibility') as ReportTypeId),
  ])];
  const calculationPlan = buildReportPlan({
    ...args.analysisContext,
    site: decisionSite,
    sed,
    reports,
    intent,
    powerAvailabilityPreferences,
    requiresGenerationImpact: applicability.required,
    requiredTypes: mandatory,
    scopeTypes: calculationTypes,
    preflightByType: new Map(),
  });
  const calculationPlanByType = new Map(
    calculationPlan.map(item => [item.type, item] as const),
  );
  const reportForCalculation = (type: ReportTypeId): SiteRagReport | null => {
    const reportId = calculationPlanByType.get(type)?.calculationReportId;
    return (reportId ? reports.find(report => report.id === reportId) : null)
      ?? latestUsableSavedReportOfType(reports, type);
  };
  const latestNonDraftReportOfType = (type: ReportTypeId): SiteRagReport | null => {
    const matches = reports
      .filter(
        report =>
          (report.reportType ?? 'feasibility') === type &&
          report._metadataOnly !== true &&
          report.generationTier !== 'draft' &&
          report.isDraft !== true,
      )
      .sort((a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? ''));
    return matches[0] ?? null;
  };
  const tierSeverity = (tier: VerdictTier): number =>
    tier === 'red' ? 3 : tier === 'amber' ? 2 : tier === 'green' ? 1 : 0;
  const qualifyingTypes = new Set<ReportTypeId>();
  const completedScreeningTypes = new Set<ReportTypeId>();

  // Build the per-source verdict matrix (one row per mandatory type) up front so
  // it is always reported, withheld or not.
  for (const type of mandatory) {
    const planItem = calculationPlanByType.get(type);
    const calcReport = reportForCalculation(type);
    const latestReport = latestNonDraftReportOfType(type);
    const displayIntent = site.generationAssessmentSelection?.kind === 'grid-led-standby-planning' ? intent : undefined;
    const calcVerdict = calcReport ? getDisplayVerdict(calcReport, displayIntent) : null;
    const latestVerdict = latestReport ? getDisplayVerdict(latestReport, displayIntent) : null;
    const useLatestVerdict = Boolean(
      latestVerdict && (!calcVerdict || tierSeverity(latestVerdict.tier) > tierSeverity(calcVerdict.tier)),
    );
    const report = useLatestVerdict ? latestReport : calcReport;
    const v = useLatestVerdict ? latestVerdict : calcVerdict;
    const qualifies = Boolean(
      planItem?.contributesToCalculations === true || (useLatestVerdict && v && v.tier !== 'none' && v.tier !== 'green'),
    );
    if (planItem?.contributesToCalculations === true && planItem.lifecycle !== 'quality-held') {
      qualifyingTypes.add(type);
    }
    if (
      planItem
      && planItemCompletesStage(planItem)
      && planItem.lifecycle !== 'quality-held'
      && planItem.lifecycle !== 'preview'
      && reportTypeStage(type) === 1
    ) {
      completedScreeningTypes.add(type);
    }
    if (report && v) {
      // Q12 / D6 — the DISPLAY verdict, identical to the one the report's own
      // cover and body render. For a financial source that means: the engine
      // band is the deterministic tier, the LLM `verdictLean` is EXEMPT (see
      // `VERDICT_LEAN_EXEMPT_REPORT_TYPES`), and the monotonic-down caps are
      // already applied — so this matrix can never disagree with the report it
      // is citing. The MANDATORY gating above (which types get a row at all)
      // is deliberate and unchanged: a financial verdict floors the rollup only
      // when the intent makes the financial report mandatory.
      perSourceVerdicts.push({
        reportType: type,
        tier: v.tier,
        label: v.label,
        present: true,
        qualifies,
        mandatory: true,
      });
    } else {
      perSourceVerdicts.push({
        reportType: type,
        tier: 'none',
        label: '—',
        present: false,
        qualifies: false,
        mandatory: true,
      });
    }
  }

  // ── STAGE 1.5 — NON-MANDATORY generated sources also carry floor authority. ──
  //
  // This matrix used to be built over `mandatory` alone, which meant a RED
  // environmental, water or sentiment report on a site whose intent never
  // triggered its gate could not move the call by a single band. The composite
  // engine already disagreed: `computeSourceFloor` (portfolio/sourceFloor.ts)
  // considers EVERY present type and resolves a non-mandatory one as mitigable
  // — so one report produced two different calls depending on which engine you
  // asked, which that module's own comment calls "worse than either call
  // alone". This closes the gap in the direction the composite already took.
  //
  // Deliberately mirrors `computeSourceFloor` rather than inventing a domain:
  // considered types are the ones actually PRESENT on the site, advisory types
  // are excluded by the same `isAdvisoryReportType` predicate the composite
  // uses (so connectivity cannot gate here while being informational there),
  // and `qualifies` is computed identically — a stale non-mandatory report caps
  // no more than a stale mandatory one.
  {
    const consideredTypes = calculationTypes;
    for (const type of consideredTypes) {
      if (mandatory.includes(type)) continue;
      if (isAdvisoryReportType(type)) continue;
      const report = reportForCalculation(type);
      if (!report) continue;
      const v = getDisplayVerdict(report, site.generationAssessmentSelection?.kind === 'grid-led-standby-planning' ? intent : undefined);
      perSourceVerdicts.push({
        reportType: type,
        tier: v.tier,
        label: v.label,
        present: true,
        qualifies: calculationPlanByType.get(type)?.contributesToCalculations === true,
        mandatory: false,
      });
    }
  }
  const mandatorySatisfied = mandatory.every((type) => qualifyingTypes.has(type));
  const stageOf = (stage: 1 | 2): DecisionStageRollup => {
    const requiredTypes = mandatory.filter((type) => reportTypeStage(type) === stage);
    const stageCurrentTypes = stage === 1 ? completedScreeningTypes : qualifyingTypes;
    const qualifiedTypes = requiredTypes.filter((type) => stageCurrentTypes.has(type));
    const outstandingTypes = requiredTypes.filter((type) => !stageCurrentTypes.has(type));
    const locked = stage === 2 && (decisionSite.generationAssessmentSelection?.kind === 'grid-led-standby-planning'
      ? selectedGridLedPlanningReadinessReason(decisionSite) !== null
      : applicability.status !== 'grid-only'
        && resolvePromotedGenerationScenario(intent, decisionSite.promotedGenerationScenarioSnapshot, null, decisionSite.generationAssessmentSelection, decisionSite.generationProjectInputBinding) === null);
    const fromComposite = stage === 1 ? composite?.decisionStages?.screening : undefined;
    // Mirror the composite's screening score only when that composite saw
    // every latest Stage 1 report — a portfolio composite persisted before a
    // re-run would otherwise pair yesterday's score with today's result.
    const latestStage1Ids = stage === 1
      ? requiredTypes
          .map(type => calculationPlanByType.get(type)?.calculationReportId)
          .filter((id): id is string => Boolean(id))
      : [];
    // A composite that cannot say what it saw (an epoch-stamped stale
    // placeholder carries no `sourceReportIds`) is never fresh — no score, no throw.
    const seenReportIds = composite?.sourceReportIds;
    const compositeFresh = Array.isArray(seenReportIds) && latestStage1Ids.every((id) => seenReportIds.includes(id));
    const scored = fromComposite?.score != null && compositeFresh
      ? { score: fromComposite.score, scoreCoverage: fromComposite.scoreCoverage ?? 0 }
      : {};
    const base: DecisionStageRollup = { stage, requiredTypes, qualifiedTypes, outstandingTypes, complete: outstandingTypes.length === 0 && !locked, locked, ...scored };
    if (stage !== 1 || requiredTypes.length === 0) return base;
    const rows = requiredTypes.map((type) => perSourceVerdicts.find((row) => row.reportType === type));
    if (rows.some((row) => !row || !row.present)) return base;
    // A red DECLINES only under a 'hard' floor policy (the PATH 2 policy); a
    // MITIGABLE red caps at CONDITIONAL, so it is a concern, never a decline.
    const hardRed = requiredTypes.filter((type, i) => rows[i]?.tier === 'red' && sourceFloorPolicy(type) === 'hard');
    const softRed = requiredTypes.filter((type, i) => rows[i]?.tier === 'red' && sourceFloorPolicy(type) !== 'hard');
    const amber = requiredTypes.filter((_, i) => rows[i]?.tier === 'amber');
    if (hardRed.length + softRed.length + amber.length === 0 && (rows.some(row => row?.tier === 'none') || !base.complete)) return base;
    const outcome: ScreeningOutcome = hardRed.length > 0 ? 'declined' : amber.length + softRed.length > 0 ? 'concerns' : 'clear';
    return { ...base, outcome, concernTypes: [...hardRed, ...softRed, ...amber] };
  };
  const stages: DecisionStagesRollup = { screening: stageOf(1), validation: stageOf(2) };
  const firmCapacityVerdict = summarizeIntentFeasibility(intent).worstVerdict;

  // ── ADVISORY source rows (displayed, never counted). ────────────────────────
  // CONNECTIVITY-ADVISORY — when the Connectivity Feasibility Study exists but
  // is not in the mandatory set (i.e. while `CONNECTIVITY_GATES_GO` is false,
  // or the intent never signalled it), its display verdict still deserves a
  // row on the decision guide — silent disappearance would read as the report
  // never having been run. The row lives in `advisoryVerdicts`, which no PATH
  // iterates, so it can never move the band. Built before the withheld
  // short-circuits (like the mandatory matrix) so it is always reported.
  // Absent report → no row (no-assert-on-no-evidence).
  const advisoryVerdicts: PerSourceVerdictRow[] = [];
  if (!mandatory.includes('interconnection')) {
    const connectivityReport = reportForCalculation('interconnection');
    if (connectivityReport) {
      const v = getDisplayVerdict(connectivityReport, site.generationAssessmentSelection?.kind === 'grid-led-standby-planning' ? intent : undefined);
      advisoryVerdicts.push({
        reportType: 'interconnection',
        tier: v.tier,
        label: v.label,
        present: true,
      });
    }
  }

  if (!adequacy.adequate) {
    const reason = adequacy.reason ?? 'data behind the verdict is not yet adequate';
    bindingConstraints.push(`Insufficient evidence — ${reason}`);
    // The parenthetical used to append `coverageRatio` — the pillar-COUNT ratio
    // — beside `reason`, which since the weight gate landed may carry a
    // DIFFERENT percentage measuring a different thing. A feasibility-only site
    // rendered "Only 60% of decision weight is measured (56% pillar coverage)":
    // two numbers in one sentence, neither labelled well enough to tell them
    // apart, shipped into an exported Decision Guide that cannot be corrected
    // after capture. The reason already states its own figure and what it
    // measures, so the count is dropped here rather than restated ambiguously.
    rationale.push(
      `Verdict withheld — ${reason}. A defensible GO/NO-GO call cannot be asserted yet.`,
    );
    return {
      band: null,
      label: base.bandLabel === '—' ? VERDICT_WITHHELD_LABEL : base.bandLabel,
      rationale,
      bindingConstraints,
      perSourceVerdicts,
      advisoryVerdicts,
      firmCapacityVerdict,
      adequacy,
      mandatorySatisfied,
      stages,
    };
  }

  // ── PATH 1b — DECISION-DEFINING POWER STRATEGY (withhold, never fail). ──────
  // Physical phase content can make Generation & Impact applicable, but it
  // cannot silently answer the accountable grid / hybrid / BTM-only decision.
  // Missing intent is an absence of decision authority, not evidence of a
  // technical NO-GO, so the strict recommendation is withheld until Define is
  // complete. This prevents both GO and CONDITIONAL from being presented as a
  // final system recommendation while still allowing an early human NO-GO or
  // DEFER record.
  if (!applicability.strategyDeclared) {
    const note =
      'Power strategy undeclared — confirm grid-only, hybrid, or BTM-only before recording GO or CONDITIONAL.';
    bindingConstraints.push(note);
    rationale.push(
      'System recommendation withheld — the accountable grid-versus-on-site power strategy has not been declared.',
    );
    return {
      band: null,
      label: 'Decision pending',
      rationale,
      bindingConstraints,
      perSourceVerdicts,
      advisoryVerdicts,
      firmCapacityVerdict,
      adequacy,
      mandatorySatisfied,
      stages,
    };
  }
  let band: DerivedBand = base.band;
  let label = base.bandLabel;
  rationale.push(
    `Base decision band is ${base.bandLabel} from the live site composite (${Math.round(adequacy.coverageRatio * 100)}% pillar coverage).`,
  );

  // ── PATH 0b — UNMEASURED-DILIGENCE ATTRIBUTION (explains, never lowers). ────
  // R1 subtracts an unmeasured-pillar penalty from the composite, so absent
  // diligence can move the BASE band (PATH 0) rather than fire a cap. Base-band
  // movement emits no binding constraint, which left a real hole: a site could
  // read a whole band worse with NOTHING in `bindingConstraints` naming why —
  // and the Resolve stage renders that array as the user's blocker list.
  //
  // This note is ATTRIBUTION, not a cap: it never touches `band`. It fires only
  // when the unmeasured term alone is enough to cost the site its 'V' tag,
  // reusing DATA_CONFIDENCE_TAG_VERIFIED_MAX as the materiality bar so a site
  // with one thin pillar is not nagged. Sites with full coverage emit nothing.
  {
    const unmeasuredPenalty =
      composite?.dataConfidenceBreakdown?.penalties?.unmeasuredPillarWeight ?? 0;
    const unmeasuredWeight = composite?.dataConfidenceBreakdown?.unmeasuredPillarWeight ?? 0;
    if (unmeasuredPenalty >= PORTFOLIO_CONFIG.DATA_CONFIDENCE_TAG_VERIFIED_MAX && unmeasuredWeight > 0) {
      // CONNECTIVITY-ADVISORY — only STATIC advisory pillars are excluded from
      // the unmeasured-weight charge, so "Unmeasured diligence" must name
      // exactly the pillars that charge covers. This filter has to use the SAME
      // frame as `unmeasuredPillarWeight` in compositeScorer: filtering on
      // applicability instead excluded precisely the pillars the charge now
      // adds (an inactive Financial + Supply Chain are charged 0.23 but are
      // `not-applicable`), so the note could fire naming ZERO pillars and fall
      // back to generic copy — a Resolve-stage blocker pointing at nothing.
      const missingPillars = Object.values(composite?.pillars ?? {})
        .filter((p) => p?.missing && !isAdvisoryPillar(p.pillarId))
        .map((p) => p.pillarId === 'water'
          ? 'Water screening (Early Screening Assessment chapter)'
          : PILLARS[p.pillarId]?.label ?? p.pillarId);
      const note = missingPillars.length > 0
        ? `Unmeasured diligence — missing supporting evidence for ${missingPillars.join(', ')}; unmeasured decision pillars reduce the site composite.`
        : 'Unmeasured diligence — decision pillars without supporting evidence reduce the site composite.';
      bindingConstraints.push(note);
      rationale.push(note);
    }
  }

  // ── PATH 0c — ON-SITE GENERATION ATTRIBUTION (explains, never lowers). ─────
  // The promoted Generation & Impact scenario used to reach this decision only
  // laundered through Project Intent phase MW (promotion rewrites
  // `powerSourceConfig.componentParams[].capacityMW`, and `firmCapacityVerdict`
  // above reads `intent.phases` alone). Its N-k load and reserve margins, its
  // joint capacity screen and its own red resource risks were all stripped on
  // the way — so a site whose promoted fleet MISSES its reserve requirement
  // could present a decision with nothing naming that.
  //
  // Deliberately attribution, not a cap, exactly like PATH 0b above: how
  // generation should WEIGH against the other decision pillars is a product
  // judgement, and silently re-banding existing sites is the wrong way to make
  // it. `deriveGenerationDecisionContribution` speaks only for a CURRENT
  // promoted scenario — `resolvePromotedGenerationScenario` returns null unless
  // the id and both integrity hashes still match the intent stamps — so a stale
  // or absent basis leaves this decision byte-identical to before.
  {
    const promotedGeneration = resolvePromotedGenerationScenario(
      intent,
      decisionSite.promotedGenerationScenarioSnapshot,
      null,
      decisionSite.generationAssessmentSelection,
      decisionSite.generationProjectInputBinding,
    );
    const contribution = deriveGenerationDecisionContribution(promotedGeneration);
    for (const note of contribution?.notes ?? []) {
      bindingConstraints.push(note);
      rationale.push(note);
    }
  }

  // ── PATH 0d — CONNECTIVITY ADVISORY ATTRIBUTION (explains, never moves). ────
  // CONNECTIVITY-ADVISORY — when a Connectivity Feasibility Study exists as an
  // advisory (non-mandatory) source, say so in the rationale so the user can
  // see WHY its reading — good or bad — does not appear among the caps.
  // Rationale-only, deliberately NOT `bindingConstraints`: that array feeds the
  // Resolve-stage blocker list and the "Binding constraints" surfaces, and an
  // advisory note constrains nothing (PATHs 0b/0c push there because they
  // explain a real base-band reduction; this explains an exclusion).
  for (const row of advisoryVerdicts) {
    rationale.push(
      `Connectivity Feasibility (advisory) — reads ${row.label}; connectivity is `
      + 'informational and does not gate the GO/NO-GO call (insufficient connectivity '
      + 'data coverage).',
    );
  }

  // ── PATH 2 — PER-SOURCE FLOOR (can only LOWER). ──────────────────────────────
  // Each PRESENT, QUALIFYING mandatory source imposes its own ceiling, resolved
  // by `sourceFloorPolicy(type)`:
  //   - 'hard'      sources (physics / permitting / financeability) map red to
  //                 NO-GO and amber to CONDITIONAL;
  //   - 'mitigable' sources (connectivity, procurement, sentiment) cap at
  //                 CONDITIONAL for BOTH red and amber — the finding is real and
  //                 still blocks an unconditional GO, but a reversible problem
  //                 must not be the sole author of a NO-GO.
  // A 'none' tier (unscored / absent) is NEUTRAL and skipped, so an unscored
  // source never silently floors the headline.
  //
  // The strongest resulting ceiling wins. Only the ceiling that ACTUALLY lowers
  // the running band records a binding constraint (no stacking of no-op caps).
  {
    let strongestCeiling: Exclude<DerivedBand, null> | null = null;
    let strongestRow: PerSourceVerdictRow | null = null;
    for (const row of perSourceVerdicts) {
      if (!row.present || row.qualifies === false) continue;
      if (row.tier === 'none' || row.tier === 'green') continue;
      // STAGE 1.5 — a source the intent never made mandatory caps at
      // CONDITIONAL whatever its own policy says. It holds real evidence, so it
      // must block an unconditional GO; but it was never designated part of the
      // decision gate, so it may not decline the site on its own. Identical to
      // `computeSourceFloor`'s `nonMandatory || policy === 'mitigable'`.
      const policy = row.mandatory === false
        ? 'mitigable' as const
        : sourceFloorPolicy(row.reportType);
      const ceiling = tierToCapBand(row.tier, policy);
      if (strongestCeiling === null || rankBand(ceiling) < rankBand(strongestCeiling)) {
        strongestCeiling = ceiling;
        strongestRow = row;
      }
    }
    if (strongestCeiling !== null && rankBand(strongestCeiling) < rankBand(band)) {
      band = strongestCeiling;
      const worstType = strongestRow?.reportType ?? '';
      const worstLabel = strongestRow?.label ?? '';
      const mitigable = strongestRow
        ? sourceFloorPolicy(strongestRow.reportType) === 'mitigable'
        : false;
      // Name the policy in the note so a CONDITIONAL from a mitigable red is
      // distinguishable from a CONDITIONAL from a hard amber — otherwise the
      // two read identically in the rationale and the user cannot tell whether
      // the constraint is addressable.
      const note = mitigable
        ? `Source floor — the ${worstType} report reads ${worstLabel}; a mitigable constraint caps the overall call at CONDITIONAL.`
        : `Source floor — the ${worstType} report reads ${worstLabel}; the overall call cannot exceed it.`;
      bindingConstraints.push(note);
      rationale.push(note);
    }
  }

  // ── PATH 3 — FIRM-CAPACITY CAP (can only LOWER). ─────────────────────────────
  // Use the SAME mechanism getDisplayVerdict runs — capTierByFirmCapacity — so
  // the cap note is byte-identical to every other surface (consistency mandate).
  // Feed it the running band's tier-equivalent; null/feasible → no cap.
  {
    // Map the running band to a cap tier so the cap evaluates against the CURRENT
    // ceiling (a no-go can't be re-capped).
    //
    // BOTH conditional bands map to 'amber'. `conditional-high` used to fall
    // through to 'green' here — the same inversion BAND_RANK carried — which
    // made the WORSE of the two conditional bands evaluate as if the site were
    // healthy, so the firm-capacity cap under-fired on exactly the sites that
    // most needed it.
    const runningTier: VerdictTier =
      band === null ? 'none'
      : band === 'no-go' ? 'red'
      : band === 'conditional' || band === 'conditional-high' ? 'amber'
      : 'green';
    const { cap } = capTierByFirmCapacity(runningTier, intent);
    if (cap) {
      const ceilingBand: Exclude<DerivedBand, null> =
        firmCapacityVerdict === 'infeasible' ? 'no-go' : 'conditional';
      if (rankBand(ceilingBand) < rankBand(band)) {
        band = ceilingBand;
        bindingConstraints.push(cap.note);
        rationale.push(cap.note);
      }
    }
  }

  // ── PATH 5 — SIMULATION-FEASIBILITY CAP (can only LOWER). ────────────────────
  // A PRESENT-but-poor sim caps the verdict; an ABSENT sim is a NEUTRAL no-op
  // (no-assert-on-no-evidence — a site with no sim is byte-identical to today).
  // Slotted AFTER PATH 3 (firm-capacity/ELCC) and BEFORE PATH 4 (completeness):
  // it is a PHYSICS-of-the-engine feasibility cap (grouped with PATH 3) and is
  // STRONGER than PATH 4's absence-cap, so it must run first so PATH 4 then sees
  // the already-lowered band and cannot double-count (strict-less-than guard).
  //
  // Sub-rules are evaluated worst→least-severe; the SINGLE strongest sim cap
  // that actually lowers the band records ONE binding constraint (no stacking).
  // `converged===undefined` / non-finite fields are NEUTRAL (never a failure).
  {
    const simCap = resolveSimFeasibilityCap(simEvidence);
    if (simCap && rankBand(simCap.ceiling) < rankBand(band)) {
      band = simCap.ceiling;
      bindingConstraints.push(simCap.note);
      rationale.push(simCap.note);
    }
  }

  // ── PATH 6 — CRITICAL-FINDING CAP (can only LOWER, never to NO-GO). ──────────
  // An UNMITIGATED `severity: 'critical'` RiskItem on a QUALIFYING, PRESENT
  // decision source caps the call at CONDITIONAL.
  //
  // Why this exists: before R4 a finding could not move the band at all. The
  // only channels were prose regex-scraped for NUMBERS, and QA blocks that
  // lowered a pillar's WEIGHT rather than its score. A report could state
  // "CRITICAL: no water rights, cannot be permitted" and the headline stayed GO.
  //
  // Why it is a CEILING and never NO-GO: `RiskItem` rows are LLM-authored. The
  // severity enum is schema-constrained (so it is structured data, not prose),
  // which is what makes it admissible under the "deterministic scoring is
  // authoritative; narrative may not raise, lower, or replace the computed
  // verdict" doctrine — but admissible is not the same as decisive. Letting a
  // generated row assert NO-GO would hand narrative the strongest verdict in
  // the system. CONDITIONAL says "a named critical exposure is open"; the
  // deterministic scorers keep sole authority to say the project is dead.
  //
  // Runs BEFORE PATH 4 for the same reason PATH 5 does: it is the stronger cap,
  // so PATH 4's strict-less-than guard then prevents double-counting.
  {
    const criticalRows: string[] = [];
    for (const row of perSourceVerdicts) {
      if (!row.present || row.qualifies === false) continue;
      const report = reportForCalculation(row.reportType);
      if (!report) continue;
      for (const label of unmitigatedCriticalRiskLabels(report)) {
        criticalRows.push(`${row.reportType}: ${label}`);
      }
    }
    if (criticalRows.length > 0 && rankBand('conditional') < rankBand(band)) {
      band = 'conditional';
      const shown = criticalRows.slice(0, 3).join('; ');
      const more = criticalRows.length > 3 ? ` (+${criticalRows.length - 3} more)` : '';
      const note = `Unmitigated critical finding${criticalRows.length === 1 ? '' : 's'} — ${shown}${more}; cannot assert an unconditional GO until mitigated.`;
      bindingConstraints.push(note);
      rationale.push(note);
    }
  }

  // ── PATH 4 — MANDATORY-COMPLETENESS CAP (can only LOWER, never to NO-GO). ─────
  // A missing mandatory report is an ABSENCE, not a fail — it caps at CONDITIONAL
  // only (never NO-GO), which is why it is the WEAKEST (last) cap. It fires only
  // when the running band is currently BETTER than conditional (go / conditional-
  // high). Never raises.
  if (!mandatorySatisfied && rankBand(band) > rankBand('conditional')) {
    const missingTypes = mandatory.filter((t) => !qualifyingTypes.has(t));
    band = 'conditional';
    const note = `Required diligence incomplete — ${missingTypes.join(', ')} has no usable successful result; cannot assert an unconditional GO.`;
    bindingConstraints.push(note);
    rationale.push(note);
  }

  // Final label — when the band was lowered off the base, re-derive a human
  // label off the resolved band (the base bandLabel only matches base.band).
  label = resolveBandLabel(band, base.bandLabel);

  // TWO-STAGE HOLD (Phase 2, owner decisions 2 and 4) — runs LAST, after every
  // cap and floor, and withholds only a band ABOVE no-go: a hard decline stands
  // (a red required analysis is complete work with a red answer, and "Decision
  // pending" must never hide a NO-GO). Anything better than no-go waits until
  // early screening is complete, a scenario is promoted (unless grid-only) and
  // mature validation is complete. Monotonic-down by construction.
  if (args.twoStageHold !== false && band !== 'no-go') {
    const labelOf = (types: readonly ReportTypeId[]): string =>
      types.map((type) => {
        const defaultLabel = REPORT_TYPE_REGISTRY[type]?.shortLabel ?? type;
        const coverageRefusal = enqueueCoverageRefusalFor(type, sed, {
          dispatchWaivers: site.siteConfigOverrides?.providerCoverageDispatchWaivers,
          auditOverrides: site.auditOverrides,
        });
        if (coverageRefusal) {
          return `${defaultLabel} (Blocked: ${coverageRefusal.message})`;
        }
        return defaultLabel;
      }).join(', ');
    const holdNote =
      !stages.screening.complete
        ? `Early screening incomplete — ${labelOf(stages.screening.outstandingTypes)} needs a usable successful result before a band can be published.`
        : stages.validation.locked
          ? 'Mature validation needs a promoted generation scenario — solve and promote one before a band can be published.'
          : !stages.validation.complete
            ? `Mature validation incomplete — ${labelOf(stages.validation.outstandingTypes)} needs a usable successful result before a band can be published.`
            : null;
    if (holdNote) {
      band = null;
      label = 'Decision pending';
      // PATH 4's completeness cap is subsumed: the stage note names the same
      // absent or stale required work with its stage, so the cap's own note
      // would only repeat it. Replace, never stack.
      const path4 = (note: string): boolean => /^Required diligence incomplete/.test(note);
      for (const list of [bindingConstraints, rationale]) {
        for (let i = list.length - 1; i >= 0; i -= 1) if (path4(list[i])) list.splice(i, 1);
      }
      bindingConstraints.push(holdNote);
      rationale.push(`Decision pending — ${holdNote}`);
    }
  }
  return {
    band,
    label,
    rationale,
    bindingConstraints,
    perSourceVerdicts,
    advisoryVerdicts,
    firmCapacityVerdict,
    adequacy,
    mandatorySatisfied,
    stages,
  };
}

/**
 * PATH 5 — resolve the single STRONGEST simulation-feasibility cap that a
 * present-but-poor sim imposes, or `null` when the sim is absent / good / its
 * fields are non-finite (NEUTRAL — no cap, no note).
 *
 * Returns the band CEILING the sim imposes plus the one binding-constraint note
 * to record. The caller applies it only when it would STRICTLY lower the running
 * band (monotonic-down, no double-cap). Sub-rules are evaluated worst→least so
 * the lowest ceiling wins and only its note is recorded (no stacking).
 *
 * S2 (LIVE): converged===false, convergenceRate<floor, capacityFactor<floor.
 * S3 (LIVE): Rule 4 caps on the closure-residual signal — `annualYieldToPathway`
 * threads the EO conservation residuals into `sim.closureResidualRel`, and the
 * rule applies the CLOSURE_RESIDUAL_TOLERANCE cap when that residual exceeds it.
 */
function resolveSimFeasibilityCap(
  sim: SimQualityEvidence | null | undefined,
): { ceiling: Exclude<DerivedBand, null>; note: string } | null {
  if (!sim) return null;

  // Rule 1 — HARD NON-CONVERGENCE (strongest). Explicit `false` only; undefined
  // is NEUTRAL (unknown convergence is never treated as a failure).
  if (sim.converged === false) {
    return {
      ceiling: HARD_NONCONVERGENCE_CEILING,
      note:
        'Simulation did not converge — the modelled plant cycle did not close; '
        + 'the headline cannot assert an unconditional GO until the solve converges.',
    };
  }

  // Rule 2 — LOW CONVERGENCE RATE. Finite + below the floor.
  if (Number.isFinite(sim.convergenceRate) && (sim.convergenceRate as number) < CONVERGENCE_RATE_FLOOR) {
    const pct = Math.round((sim.convergenceRate as number) * 100);
    const floorPct = Math.round(CONVERGENCE_RATE_FLOOR * 100);
    return {
      ceiling: 'conditional',
      note:
        `Simulation converged on only ${pct}% of evaluated hours (floor ${floorPct}%); `
        + 'the annual yield is partially modelled — cannot assert an unconditional GO.',
    };
  }

  // Rule 3 — BELOW CAPACITY-FACTOR FLOOR. Finite + below the structural floor.
  if (Number.isFinite(sim.capacityFactor) && (sim.capacityFactor as number) < CAPACITY_FACTOR_FLOOR) {
    return {
      ceiling: 'conditional',
      note:
        `Modelled capacity factor ${(sim.capacityFactor as number).toFixed(2)} is below the structural `
        + `floor ${CAPACITY_FACTOR_FLOOR.toFixed(2)} — LCOE is dominated by fixed-cost-per-MWh and the `
        + 'economics are structurally suspect (mirrors the BoP economics-hook warning). Cannot assert an '
        + 'unconditional GO.',
    };
  }

  // Rule 4 — HIGH CLOSURE RESIDUAL (S3, LIVE). Caps on the EO-conservation
  // residual that `annualYieldToPathway` threads into `sim.closureResidualRel`.
  if (Number.isFinite(sim.closureResidualRel) && (sim.closureResidualRel as number) > CLOSURE_RESIDUAL_TOLERANCE) {
    return {
      ceiling: 'conditional',
      note:
        `Energy/mass closure residual ${(sim.closureResidualRel as number).toFixed(4)} exceeds the `
        + `conservation tolerance ${CLOSURE_RESIDUAL_TOLERANCE.toFixed(4)} — the modelled cycle does not `
        + 'conserve within tolerance; the yield is not trustworthy enough to assert an unconditional GO.',
    };
  }

  return null;
}

/**
 * Human label for the FINAL band. When the band still equals the base band's
 * tone we keep the config-resolved `baseBandLabel` (so a custom band config's
 * label flows through); when a cap lowered it we fall back to the canonical
 * label for the resolved band.
 */
function resolveBandLabel(band: DerivedBand, baseBandLabel: string): string {
  if (band === null) return baseBandLabel === '—' ? VERDICT_WITHHELD_LABEL : baseBandLabel;
  // Canonical labels mirror the DEFAULT_BANDS labels (decisionBands.ts).
  // Read from the ONE canonical source (decisionBands). This map only bridges
  // the rollup's band spelling ('no-go', 'conditional-high') to the registry's
  // ('noGo', 'conditionalHigh') — the two vocabularies for one concept.
  const CANONICAL: Record<Exclude<DerivedBand, null>, string> = {
    go: DECISION_BAND_LABEL.go,
    'conditional-high': DECISION_BAND_LABEL.conditionalHigh,
    conditional: DECISION_BAND_LABEL.conditional,
    'no-go': DECISION_BAND_LABEL.noGo,
  };
  // Prefer the base label only when it already describes THIS band tone (the
  // base stood). Otherwise use the canonical label for the lowered band.
  // Case-INSENSITIVE on both sides. The comparison previously upper-cased only
  // the base label, which silently assumed the canonical labels were uppercase
  // ("GO"). With sentence-case labels that test could never match, so the base
  // label would always be discarded even when the base band stood.
  return baseBandLabel
    && baseBandLabel !== '—'
    && baseBandLabel.toUpperCase() === CANONICAL[band].toUpperCase()
    ? baseBandLabel
    : CANONICAL[band];
}
